//@ts-check
import esriRequest from "@arcgis/core/request";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import LocatorSearchSource from "@arcgis/core/widgets/Search/LocatorSearchSource";
import Locator from "@arcgis/core/tasks/Locator";
import Locate from "@arcgis/core/widgets/Locate";
import Search from "@arcgis/core/widgets/Search";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import ActionButton from "@arcgis/core/support/actions/ActionButton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArcGISMap from "./components/ArcGISMap/ArcGISMap";
import { LocallyProjectingLocateViewModel } from "./widgets/Locate/LocallyProjectingLocateViewModel";
import Point from "@arcgis/core/geometry/Point";
import { px2pt } from "@arcgis/core/core/screenUtils";
import Graphic from "@arcgis/core/Graphic";
import * as projection from "@arcgis/core/geometry/projection";
import Extent from "@arcgis/core/geometry/Extent";
import Basemap from "@arcgis/core/Basemap";
import Expand from "@arcgis/core/widgets/Expand";
import ReactDOM from "react-dom";
import { getOsBearerToken } from "../../helpers/authHelper";
import { addInterceptors } from "./addInterceptors";
import { osRoadLayerId, osVectorLayerId, satelliteLayerId } from "../../constants/mapLayers"; 
import { satelliteLayer, osRoadLayer, osVectorLayer, getLayerText } from "../../helpers/map/mapLayerHelper"; 
import { generateListEntryPopup }  from "../../helpers/map/popupHelper"; 
import { updateQueryString } from "../../helpers/urlHelper";
import { getNewItem, lookForItemByAddress } from "../../app/API/commentAPI";
import { createPopupContent } from "../../helpers/map/popupHelper";

const { REACT_APP_SIGNINURL } = process.env;
const { REACT_APP_IMAGEPATH } = process.env;
const pin = `${REACT_APP_IMAGEPATH}/map-pin.svg`;
const bluePin = `${REACT_APP_IMAGEPATH}/map-pin--blue.svg`;
const osRoadThumbnail = `${REACT_APP_IMAGEPATH}/OSRoad_thumbnail.png`;
const osVectorTileThumbnail = `${REACT_APP_IMAGEPATH}/Default_OSVectorTile_thumbnail.png`;
const satelliteThumbnail = `${REACT_APP_IMAGEPATH}/Satellite-APGB_thumbnail.png`
const searchIcon = `${REACT_APP_IMAGEPATH}/search-icon.svg`;
const signInRedirect = `${REACT_APP_SIGNINURL}/?u=&referrer=${window.location}`;

const maxResults = 30; 

const pinSymbol = new PictureMarkerSymbol({
  height: px2pt(35),
  width: px2pt(22),
  yoffset: px2pt(32 / 2),
  url: pin,
});

const searchSymbol = new PictureMarkerSymbol({
  height: px2pt(80),
  width: px2pt(64),
  yoffset: px2pt(80 / 2),
  xoffset: px2pt(64 / 2),
  url: searchIcon,
});

export const MainMap = (props) => {
  const {
    mapdata,
    bounds,
    centre,
    zoom,
    minimumZoom,
    allowUsersToSetPins,
    handleIdChange,
    updatePopupContent,
    setShowModal,
    setItem,
    implementation,
    isUserLoggedIn,
    mergePins,
    setMergePins,
    setValidAddressForNewPin,
    currentItemId, 
    campaign
  } = props;

  addInterceptors();

  const [isAddingPin, setIsAddingPin] = useState(false);

  //variable to hold the state of item data for newly created pins.
  const newItem = useRef();
  // Map props
  // useMemo is used to avoid creating new objects on each re-render
  // causing the map to be torn down and created again on each re-render
  const containerProperties = useMemo(
    () => ({
      id: "ugc-mapview",
      className: "ugc__map ugc__map--mapview",
      "data-he-map-theming": "true"
    }),
    []
  );
  const viewProperties = useMemo(() => {
    /** @type {__esri.MapViewProperties} */
    const props = {
      center: {
        // This form of object creating is known as "autocasting", which avoids the
        // need to import dependencies, but can sometimes confuse IntelliSense (not true here)
        // https://developers.arcgis.com/javascript/latest/programming-patterns/#autocasting
        type: "point",
        x: parseInt(centre.x),
        y: parseInt(centre.y),
        spatialReference: { wkid: 27700 },
      },
      constraints: {
        geometry: new Extent({
          xmin: parseInt(bounds.ule),
          ymin: parseInt(bounds.lrn),
          xmax: parseInt(bounds.lre),
          ymax: parseInt(bounds.uln),
          spatialReference: { wkid: 27700 },
        }),
        minZoom: parseInt(minimumZoom),
        maxZoom: 18,
        rotationEnabled: false, // Disables map rotation
      },
      zoom: parseInt(zoom),
      spatialReference: { wkid: 27700 },
      ui: {
        // This controls the padding between the map's UI controls and the container.
        // Try not to use CSS for this as ArcGIS's popup positioning logic only respects the
        // value set here, not your CSS.
        padding: 10,
      },
    };
    return props;
  }, [
    centre,
    zoom,
    minimumZoom,
    bounds.ule,
    bounds.uln,
    bounds.lre,
    bounds.lrn,
  ]);

  // This builds up the HTML to be displayed in the popup.
  function popupContentPinLayer(commentCount, imageUrl, addressFound, name, loadImages, feature) {
      let containerDiv = createPopupContent(commentCount, imageUrl, addressFound, name, implementation.active, isUserLoggedIn, signInRedirect, loadImages, feature);
      let firstComment = implementation.active && addressFound && (commentCount === 0 || commentCount === undefined);

      // If a comment has not been added, but the user is logged in then when need to add an event listener
      // to redirect the user to the 'add contribution' modal if the link is clicked
      // If there are no preexisting comments it'll be the first link otherwise it will be the second link
      if (isUserLoggedIn) {
          let elem = containerDiv.getElementsByTagName('a')[firstComment ? 0 : 1]
          showModal(elem);
      }

      return containerDiv;
  }

  function showModal(elem) {
      elem.addEventListener('click', e => {
          e.preventDefault();
          setShowModal(true);
      });
    }

  function createPopupTemplate(name) {
    let template = new PopupTemplate();
    let addressFound = name !== "You cannot add a pin here";
    setValidAddressForNewPin(addressFound);
    template.actions = [];
    template.actions.push(customZoomAction);
    template.content = popupContentPinLayer(0, "", addressFound, name, false);
    template.title = name;
    return template;
  }

  async function lookForItemAndSetItemByAddress(point) {
    let address = await addressLookup(point);
    let anItem = await lookForItemByAddress(implementation.id, address);

    if (!anItem) {
      anItem = await createNewItem(implementation, address, point);
    }
    handleIdChange(anItem.externalId, true);
    newItem.current = anItem;
    
    return newItem.current; 
  }

  async function lookForItemAndSetItemByExternalId(feature) {
    let anItem = await getNewItem(implementation.id, feature.graphic.attributes.List_Entry_Number);

    if (!anItem || anItem.id === 0) {
      const point = new Point({
        x: feature.graphic.geometry.x,
        y: feature.graphic.geometry.y,
        spatialReference: { wkid: 27700 },
      });

      let address = await addressLookup(point);
    
      anItem = await createNewItem(implementation, address, point, anItem.externalId, anItem.totalComments);
    }

    newItem.current = anItem;
    
    return newItem.current; 
  }

  //popup actions
  let customZoomAction = new ActionButton({
    title: "Zoom in",
    id: "custom-zoom",
    className:
      "esri-icon-zoom-in-magnifying-glass esri-icon-zoom-in-magnifying-glass--custom-zoom",
  });

  // If an ESRI feature layer is provided this is where that layer gets created
  const dataLayer = useMemo(() => {
    // If there is no esri feature layer return null
    if (!campaign?.esriFeatureLayer) {
      return null;
    }

    // Create the popup
    let popupTemplate = new PopupTemplate();
    popupTemplate.title = "{List_Entry_Name}";
    popupTemplate.outFields = ["*"];
    popupTemplate.fieldInfos = [
      {
        fieldName: "ListEntry",
      },
    ];

    popupTemplate.content = popupContentFeatureLayer;
    popupTemplate.actions = [customZoomAction];

    // Create the layer
      let layer = new FeatureLayer({
        url: campaign?.esriFeatureLayer,
        layerId: 0,
        renderer: new SimpleRenderer({
          symbol: new PictureMarkerSymbol({
            height: px2pt(35),
            width: px2pt(22),
            yoffset: px2pt(32 / 2),
            url: bluePin,
          }),
      }),
      popupTemplate: popupTemplate,
    });
    
    return layer;
  }, [campaign?.esriFeatureLayer]);

  async function popupContentFeatureLayer(feature) {
    let newItem = await lookForItemAndSetItemByExternalId(feature); 
    return popupContentPinLayer(newItem.totalComments, newItem.itemImageUrl, true, newItem.name, true, feature);
  }

  const pinLayer = useMemo(() => {
    if(!mapdata.length) {
      return undefined; 
    }

    let graphicLayer = new GraphicsLayer();
    graphicLayer.title = "pinlayer";
    let i = 0;
      let actions = [];
      let pins = [];

      for (i = 0; mapdata.length > i; i++) {
        let popupTemplate = new PopupTemplate();
        popupTemplate.title = mapdata[i].name;
        popupTemplate.actions = [...actions];

        popupTemplate.outFields = ["*"];
        popupTemplate.fieldInfos = [
          {
            fieldName: "ListEntry",
          },
        ];
        let externalId = new URLSearchParams(window.location.search).get('id');
        popupTemplate.content = popupContentPinLayer(
          mapdata[i].totalComments,
          mapdata[i].itemImageUrl,
          true,
          mapdata[i].name,
          externalId && externalId == mapdata[i].externalId ? true : false
        );

        // Add the new custom zoom action and hide the old zoom
        popupTemplate.actions.push(customZoomAction);

        const point = new Point({
          x: mapdata[i].location.easting,
          y: mapdata[i].location.northing,
          spatialReference: { wkid: 27700 },
        });
        const pinGraphic = new Graphic({
          geometry: point,
          popupTemplate: popupTemplate,
          symbol: pinSymbol,
        });

        pinGraphic.attributes = {
          externalId: `${mapdata[i].externalId}`,
        };
        let exists = graphicLayer.graphics.find(
          (e) => e.attributes.externalId === pinGraphic.attributes.externalId
        );

        if (!exists) {
          pins.push(pinGraphic);
        }
      }

      // Add the pins to the map
      if(pins.length) {
        graphicLayer.addMany(pins); 
      } 
      
    return graphicLayer;
  }, [mapdata]);

  useEffect(() => {
    if (mergePins) {
      if (addNewPinLayer.graphics !== undefined && addNewPinLayer.graphics.length > 0) {
        let newPin = addNewPinLayer.graphics.pop();
        newPin.attributes.externalId = newPin.attributes.externalId === "-1" ? currentItemId : newPin.attributes.externalId;
        pinLayer.graphics.push(newPin);
      }
      
      setMergePins(false);
      setIsAddingPin(false);
    }
  });

  // This will be the layer that contains the pin that the user is interacting with.
  // A GraphicsLayer can have any kinds of geometry inside of it at any time, but
  // has less features when compared to a FeatureLayer (e.g. querying for geometries in a certain area must be done
  // manually)
  const locatorPinLayer = useMemo(() => new GraphicsLayer(), []);
  const addNewPinLayer = useMemo(() => new GraphicsLayer(), []);

  // Using refs as events coming out of ArcGIS are not tied to the
  // render cycle of React, so using useState just results in unnecessary
  // re-renders (negligible I know) and annoying "missing dependency" ESLint errors
  const sketchViewModelRef = useRef(
    /** @type {__esri.SketchViewModel | undefined} */ (undefined)
  );
  const dataLayerViewRef = useRef(
    /** @type {__esri.FeatureLayerView | undefined} */ (undefined)
  );
  const dataLayerViewUpdatingRef = useRef(false);
  const currentSketchViewModelUpdateEventRef = useRef(
    /** @type {__esri.SketchViewModelUpdateEvent | undefined} */ (undefined)
  );

  const mapViewRef = useRef(
    /** @type {__esri.MapView | undefined} */ (undefined)
  );

  const mapProperties = useMemo(() => {
    let layers = [];
    layers.push(locatorPinLayer);
    layers.push(addNewPinLayer);

    if (dataLayer) {
      layers.push(dataLayer);
    } else if (pinLayer) {
      layers.push(pinLayer);
    }

    /** @type {__esri.MapProperties} */
    const props = {
      basemap: {
        baseLayers: [
          osVectorLayer(implementation.mapLayerId <= osVectorLayerId ? 100 : 0),
          osRoadLayer(implementation.mapLayerId == osRoadLayerId ? 100 : 0),
          satelliteLayer(implementation.mapLayerId == satelliteLayerId ? 100 : 0)
        ],
        spatialReference: { wkid: 27700 },
      },
      layers: [...layers],
    };

    return props;
  }, [dataLayer, locatorPinLayer, addNewPinLayer, pinLayer]);

  const [addRemovePinButtonContainer, setAddRemovePinButtonContainer] = useState(() => {
    return document.createElement("div");
  });

  // Add Help Text Container
  const [addHelpTextContainer, setAddHelpTextContainer] = useState(() => {
    const helpTextDiv = document.createElement("div");
    helpTextDiv.setAttribute("class", "esri-text esri-widget");
    helpTextDiv.innerText = "Tap screen to drop a pin";
    return helpTextDiv; 
  });

  const onMapInitialized = useCallback(
    async (
      /** @type {__esri.Map} */ map,
      /** @type {__esri.MapView} */ mapView
    ) => {
      // Breakpoint values are the px width of the view, NOT the viewport
      mapView.breakpoints = {
        xlarge: 1440,
        large: 1200,
        medium: 992,
        small: 768,
        xsmall: 489
      };

      mapViewRef.current = mapView;
       
      // When the map is intialised find the pin layer and generate the popup
      let pinLayer = mapView.map.layers.items.find(x => x.title === "pinlayer");
      if(pinLayer) {
        generateListEntryPopup(mapViewRef.current, pinLayer);

        // Check for external id & if it exists handle the id change
        let externalId = new URLSearchParams(window.location.search).get('id');
        if(externalId) {
          handleIdChange(externalId);
        }
      }

      // Set the pointer to a hand when we're hovering over a pin
      mapView.on('pointer-move', function (evt) {
        const layers = [];
        layers.push(pinLayer);
        layers.push(addNewPinLayer);
        if (dataLayer) {
          layers.push(dataLayer);
        }

        mapView.hitTest(evt, { include: layers }).then(function (response) {
        const map = document.getElementById("ugc-mapview");

        if(map !== null) {
          map.style.cursor = response.results.length ? 'pointer' : 'default';
        }
        });
      });

      let searchWidget = new Search({
        sources: [
            new LocatorSearchSource({
              locator: new Locator({
                url: "https://datahub.esriuk.com/arcgis/rest/services/gb_locators/os_open_names_locator/GeocodeServer/findAddressCandidates",
              }),
              getSuggestions: async (params) => {
                return await getNamesSuggestions(params);
              },
              getResults: async (params) => {
                return await getNamesSearchResults(params);
              },
              placeholder: "Search",
              popupTemplate: {
                title: 'Search results',
                content: '{name}',
                actions: [
                  new ActionButton({
                    title: "Zoom in",
                    id: "custom-zoom",
                    className:
                      "esri-icon-zoom-in-magnifying-glass esri-icon-zoom-in-magnifying-glass--custom-zoom",
                  })
                ]
              },
              suggestionsEnabled: true,
              singleLineFieldName: "queryText",
              minSuggestCharacters: 3,
              resultSymbol: searchSymbol,
            })
          ],
        locationEnabled: false,
        view: mapView,
        includeDefaultSources: false,
      });

      mapView.when(function () {
        mapView.popup.watch("selectedFeature", function (graphic) {
          // Hide the default zoom button
          if (graphic) {
            let externalId = graphic.attributes.externalId ?? graphic.attributes.List_Entry_Number;
            // If the graphic selected is a search icon set the pin selected to false
            let pinSelected =  graphic.symbol === null || (graphic.symbol !== null && !(graphic.symbol.url == searchIcon)); 
            if (externalId) {
              updateQueryString("id", externalId);
              handleIdChange(externalId.toString(), pinSelected);
              // If there is a graphic popup template then update the content
              if(graphic.popupTemplate) {
                updatePopupContent(graphic.popupTemplate.content, externalId);
              }
            }
          }
        });
      });

      // Disable the dock & the collapse options
      // Set heading level to 4 to match NHLE
      mapView.popup.headingLevel = 4;
      mapView.popup.dockEnabled = false;
      mapView.popup.collapseEnabled = false
      mapView.popup.dockOptions = {
        buttonEnabled: false,
        breakpoint: false,
      };

      mapView.popup.viewModel.on("trigger-action", async function (event) {
        let exId = mapView.popup.viewModel.selectedFeature.getAttribute(
          "externalId"
        );
        if (event.action.id === "create-comment") {
          if (exId !== "-1") {
            handleIdChange(exId);
          }
          setShowModal(true);
        } else if (event.action.id === "create-new-comment") {
          setItem(newItem.current);
          setShowModal(true);
        } else if (event.action.id === "custom-zoom") {
          // The custom zoom is the first action on the modal
          mapView.popup.triggerAction(1);
        }
      });

      const vectorTile = new Basemap({
        title: "OS Vector",
        thumbnailUrl: osVectorTileThumbnail,
        baseLayers: [
          osVectorLayer()
        ],
        spatialReference: { wkid: 27700 },
      });

      const osRoadMap = new Basemap({
        title: "OS Road",
        thumbnailUrl: osRoadThumbnail,
        baseLayers: [
          osRoadLayer()
        ],
        spatialReference: { wkid: 27700 },
      });

      const satelliteMap = new Basemap({
        title: "Satellite Map",
        thumbnailUrl: satelliteThumbnail,
        baseLayers: [
          satelliteLayer()
        ],
        spatialReference: { wkid: 27700 },
      });

      const basemapGalleryContainerId = 'basemap-gallery-container';
      const expandCloseButtonTop = document.createElement('button');
      const expandCloseButtonWrapperTop = document.createElement('div');
      const closeIcon = document.createElement('span');
      closeIcon.classList.add('icon', 'icon--close');

      const expandHeading = document.createElement('h2');
      expandHeading.textContent = 'View';

      expandCloseButtonTop.classList.add('govuk-button', 'he-button', 'he-button__primary');
      expandCloseButtonWrapperTop.classList.add('he-expand__close-button-wrapper', 'he-expand__close-button-wrapper--top');
      expandCloseButtonTop.appendChild(closeIcon);

      expandCloseButtonWrapperTop.appendChild(expandHeading);
      expandCloseButtonWrapperTop.appendChild(expandCloseButtonTop);

      const expandCloseButtonBottom = document.createElement('button');
      const expandCloseButtonWrapperBottom = document.createElement('div');
      expandCloseButtonBottom.textContent = 'Close';
      expandCloseButtonBottom.classList.add('govuk-button', 'he-button', 'he-button__primary', 'ugc-he-expand__close-button');
      expandCloseButtonWrapperBottom.classList.add('he-expand__close-button-wrapper', 'he-expand__close-button-wrapper--bottom');
      expandCloseButtonWrapperBottom.appendChild(expandCloseButtonBottom)
      
      const expandWrapper = document.createElement('div');
      expandWrapper.id = basemapGalleryContainerId;
      expandWrapper.classList.add('ugc-he-expand__wrapper');
      expandWrapper.setAttribute('data-default', 'true'); // Init style on active basemap item

      const basemapGalleryWrapper = document.createElement('div');
      expandWrapper.appendChild(expandCloseButtonWrapperTop);
      expandWrapper.appendChild(basemapGalleryWrapper);
      expandWrapper.appendChild(expandCloseButtonWrapperBottom);

      const basemapGallery = new BasemapGallery({
        view: mapView,
        container: basemapGalleryWrapper,
        source: [vectorTile, osRoadMap, satelliteMap]
      });

      let basemapGalleryButton = new Expand({
        view: mapView,
        closeOnEsc: true,
        content: expandWrapper,
        expandIconClass: "esri-icon-layers",
      });

      basemapGallery.watch("activeBasemap", function () {
        expandWrapper.removeAttribute('data-default');
        const selectedItem = expandWrapper.querySelector(".esri-basemap-gallery__item--selected");
        selectedItem?.classList.remove("esri-basemap-gallery__item--selected");

        let mobileSize =
          mapView.heightBreakpoint === "xsmall" ||
          mapView.widthBreakpoint === "xsmall";

        if (mobileSize) {
          basemapGalleryButton.collapse();
        }
      });

      basemapGalleryButton.watch('expanded', () => {
        document.documentElement.classList.toggle('ugc__map-popup-overflow', basemapGalleryButton.expanded);
        document.body.setAttribute('data-map-expand-open', `${basemapGalleryButton.expanded}`);

        if (basemapGalleryButton.expanded) {
          expandCloseButtonTop.focus();
        }

        const listItem = document.querySelector(`[title='${getLayerText(implementation.mapLayerId)}']`)
        if(expandWrapper.hasAttribute("data-default")) {
          listItem?.classList.add("esri-basemap-gallery__item--selected");
        }
      });

      
      [expandCloseButtonBottom, expandCloseButtonTop].forEach(btn => {
        btn.type = 'button';
        btn.setAttribute('aria-controls', basemapGalleryContainerId);
        btn.addEventListener('click', () => {
          basemapGalleryButton.collapse();
        });

        // Trap focus
        btn.addEventListener('keyup', e => {
          if (e.key === 'Tab') {
            e.preventDefault();
          }
        })
        btn.addEventListener('keydown', e => {
          if (e.key === 'Tab') {
            if (btn === expandCloseButtonTop && e.shiftKey) {
              expandCloseButtonBottom.focus();
            } else if (!e.shiftKey) {
              expandCloseButtonTop.focus();
            }
          }
        });
      });

      /** @type {__esri.Graphic} */
      let locatorPinGraphic;
      const locateWidget = new Locate({
        // Use own view model that performs GPS->BritishNationalGrid conversion
        // within the browser. The default behaviour is to go off to Esri's
        // remote GeometryService.
        viewModel: new LocallyProjectingLocateViewModel({
          view: mapView,
          popupEnabled: false,
        })
      });

      locateWidget.on("locate-error", async (error) => {
        viewModel.complete();
        console.log(error)
      });

      locateWidget.on("locate", async ({ position }) => {
        viewModel.complete();
        if (!projection.isLoaded()) {
          await projection.load();
        }
      });

      // The SketchViewModel provides a nice API around editing geometries (user interaction, UI etc)
      // It needs the 'view' parameter to work, hence it being wrapped in this callback.
      let viewModel = (sketchViewModelRef.current = new SketchViewModel({
        view: mapView,
        layer: addNewPinLayer,
        tool: "point",
        updateOnGraphicClick: false,
        pointSymbol: pinSymbol,
      }));
      viewModel.on("create", async (ev) => {
        if (ev.state === "complete" && ev.graphic) {
          // Remove the help text
          mapView.ui.remove(addHelpTextContainer);  

          //hitTest is checking if the user has tried to drop a pin an an existing pin.
          const opts = {
            include: pinLayer,
          };
          let hit = await mapView.hitTest(
            mapView.toScreen(ev.graphic.geometry),
            opts
          );
          if (hit.results.length > 0) { 
            setIsAddingPin(false);
            addNewPinLayer.graphics.remove(ev.graphic);
            mapView.popup.open({
              features: [hit.results[0].graphic],
              location: hit.results[0].graphic.geometry,
            });

            return;
          }

          //if we are not dealing with a new pin on an old one do other things.
          if (!projection.isLoaded()) {
            await projection.load();
          }
          const point = /** @type {__esri.Point} */ (projection.project(
            new Point({
              x: ev.graphic.geometry.x,
              y: ev.graphic.geometry.y,
              spatialReference: { wkid: 27700 },
            }),
            mapView.spatialReference
          ));

          let anItem = await lookForItemAndSetItemByAddress(point); 

          ev.graphic.popupTemplate = createPopupTemplate(anItem.name);
          ev.graphic.attributes = {
            externalId: anItem.externalId,
          };

          // Open the popup when the base map is clicked
          mapView.popup.open({
            features: [ev.graphic],
            location: ev.graphic.geometry,
          });
        }
      });

      if (dataLayer) {
        mapView.whenLayerView(dataLayer).then((layerView) => {
          dataLayerViewRef.current = layerView;
          layerView.watch("updating", (updating) => {
            dataLayerViewUpdatingRef.current = updating;
          });
        });
      }

      mapView.ui.add(searchWidget, "top-left");
      mapView.ui.add(addRemovePinButtonContainer, "bottom-left");
      mapView.ui.add(locateWidget, "top-left");

      mapView.when(() => {
        switch (mapView.widthBreakpoint) {
          case "medium":
          case "large":
            mapView.ui.move("zoom", "top-right");
            mapView.ui.add(basemapGalleryButton, "top-right");
            break;
          default:
            mapView.ui.move("zoom", "bottom-right");
            mapView.ui.add(basemapGalleryButton, "bottom-right");
        }

        var observer = new MutationObserver(function(mutations) {
          const zoomIn = document
          .querySelector('div[title="Zoom in"]');
          const zoomOut = document
          .querySelector('div[title="Zoom out"]');
          
          if (zoomIn && zoomOut) {
               zoomIn.setAttribute('id', 'esri-zoom-in');
               zoomOut.setAttribute('id', 'esri-zoom-out');
               observer.disconnect();
          }
       });
       
       observer.observe(document, {attributes: false, childList: true, characterData: false, subtree:true});
      });

      mapView.watch("widthBreakpoint", breakpoint => {
        switch (breakpoint) {
          case "medium":
          case "large":
            mapView.ui.move(["zoom", basemapGalleryButton], "top-right");
            mapView.ui.move(locateWidget, "top-left");
            break;
          default:
            mapView.ui.move(["zoom", basemapGalleryButton], "bottom-right");
        }
      });
    },
    [dataLayer, allowUsersToSetPins, addNewPinLayer, locatorPinLayer]
  );

  async function clickHandler(event) {
    const mapView = mapViewRef.current;
    await mapView.whenLayerView(addNewPinLayer);
    const opts = {
      include: pinLayer,
    };
    let hit = await mapView.hitTest(event, opts);
    if (
      hit.results.length === 0 &&
      addNewPinLayer &&
      addNewPinLayer.graphics.length === 0 &&
      allowUsersToSetPins &&
      implementation.active
    ) {
      sketchViewModelRef.current.create("point", { mode: "click" });

      setIsAddingPin(true);

      // If it is a touch event then add the help text container
      if(('ontouchstart' in window) || (navigator.maxTouchPoints > 0)) {
        mapView.ui.add(addHelpTextContainer, "manual");
      }
    }
  }

  async function triggerPinActionEvent(event) {
    // If the user is not logged in, redirect to the login dialog
    if (!isUserLoggedIn) {
      window.location = `${REACT_APP_SIGNINURL}/?u=&referrer=${window.location}`;
    } else {
      const mapView = mapViewRef.current;
      if (isAddingPin) {
        setIsAddingPin(false);
        mapView.popup.close();
        await mapView.whenLayerView(addNewPinLayer);
        if (addNewPinLayer.graphics.length > 0) {
          addNewPinLayer.graphics.pop();
        }
        await mapView.whenLayerView(locatorPinLayer);
        if (locatorPinLayer.graphics.length > 0) {
          locatorPinLayer.graphics.pop();
        }
        mapView.ui.remove(addHelpTextContainer);    
      } else {
        setIsAddingPin(true);
        await clickHandler(event);
      }
    }

    return;
  }

  // Create the add/remove pin button
  let buttonText;
  switch (true) {
    case isAddingPin:
      buttonText = "Remove pin";
      break;
    case isUserLoggedIn:
      buttonText = "Add a pin";
      break;
    default:
      buttonText = "Sign in to add a pin";
  }
  
  const pinClassModifier = isAddingPin ? 'remove' : 'add';
  const buttonNode = ReactDOM.createPortal(
    <button
      onClick={triggerPinActionEvent}
      className={`esri-widget esri-widget--button esri-widget--map-pin esri-widget--map-pin-${pinClassModifier}`}>
      <span className={`icon icon--map-pin-${pinClassModifier}`}/>
      {buttonText}
    </button>,
    addRemovePinButtonContainer,
    addHelpTextContainer
  );

  return (
    <>
      {allowUsersToSetPins && buttonNode}
      <ArcGISMap
        view="2d"
        containerProperties={containerProperties}
        mapProperties={mapProperties}
        viewProperties={viewProperties}
        onMapInitialized={onMapInitialized}
      ></ArcGISMap>
    </>
  );
};

async function addressLookup(point) {
  let address = "";

  let response = await fetch(
    `https://api.os.uk/search/places/v1/nearest?point=${point.x},${point.y}&dataset=DPA,LPI&fq=COUNTRY_CODE:E`,
    {
      headers: {
        Authorization: `Bearer ${getOsBearerToken()}`,
      },
    }
  );
  if (response.ok) {
    let body = await response.json();
    if (body.results) {
      if (body.results[0].LPI !== undefined) {
        address = body.results[0].LPI.ADDRESS;
      } else {
        address = body.results[0].DPA.ADDRESS;
      }
    }
  }
  if (address === "") {
    address = "You cannot add a pin here";
  }
  return address;
}

async function convertCoords(point) {
  let convertedPoint = projection.project(point, { wkid: 27700 });
  return { x: convertedPoint.x, y: convertedPoint.y };
}

async function createNewItem(implementation, address, point, externalId = 0, totalComments = 0) {
  let eastNorthcoords = await convertCoords(point);

  let createdItem = {
    id: 0,
    comments: [],
    externalId: externalId === 0 ? "-1" : externalId,
    totalComments: totalComments,
    name: address,
    category: null,
    path: `${implementation.defaultItemUrl}`,
    implementationId: `${implementation.id}`,
    implementation: implementation,
    location: {
      address: address,
      easting: eastNorthcoords.x,
      northing: eastNorthcoords.y,
    },
  };
  return createdItem;
}

/**
 * Get search results with the search term provided
 * @param {*} searchTerm 
 */
async function getSearchResults(searchTerm) {
  let queryUrl = `https://datahub.esriuk.com/arcgis/rest/services/gb_locators/os_open_names_locator/GeocodeServer/findAddressCandidates?SingleLine=${encodeURI(
    searchTerm
  )}&maxLocations=${maxResults}&outFields=*&outSr=27700&f=json`
  return await esriRequest(queryUrl);
}

async function getNamesSuggestions(params) {
  let response = await getSearchResults(params.suggestTerm); 

  let suggestions = [];
  for (let i = 0; i < response.data.candidates.length && 1 < maxResults; i++) {
    // Check to see if the country is England
    if(response.data.candidates[i].attributes.Country === "England") {
      let addressText = `${response.data.candidates[i].address}`;
      let suggestion = {
        text: addressText,
        key: addressText,
        sourceIndex: 0,
      };
      suggestions.push(suggestion);
      }
    }
  return suggestions;
}

async function getNamesSearchResults(params) {
  let response = await getSearchResults(params.suggestResult.text); 
  
  const index = params.sourceIndex === undefined ? 0 : params.sourceIndex;

  if (response.data.candidates) {
    let result = response.data.candidates[index];
    let x_coordinate = result.location.x;
    let y_coordinate = result.location.y;
    let addressText = result.address;
    let graphic = new Graphic({
      geometry: new Point({
        x: x_coordinate,
        y: y_coordinate,
        spatialReference: { wkid: 27700 },
      }),
      attributes: {
        externalId: "-1",
        name: addressText,
      },
    });

    let buffer = geometryEngine.buffer(graphic.geometry, 200, "meters");

    let searchResult = {
      extent: buffer,
      feature: graphic,
      name: addressText,
    };

    return [searchResult];
  }

  return [];
}